import assert from 'assert';
import lodashGet from 'lodash/fp/get';
import lodashCompact from 'lodash/fp/compact';
import { datadogRum } from '@datadog/browser-rum';

import { USER_ROLE_ANONYMOUS } from 'common/userRoles';

import log from 'client/lib/log';
import makeApplicationContext, {
  getIsUtilityProfitApp,
  getUtilityActivationForContext,
} from 'client/analytics/makeApplicationContext';

const { NODE_ENV } = process.env;
const debug = log.debug('sunroom:analytics');

function getUser(state) {
  const isUtilityProfitApp = getIsUtilityProfitApp(state.analytics.screenId);

  if (!isUtilityProfitApp) {
    return state.currentUser;
  }

  if (state.currentUser.utilityUserId && !state.utilityUser.id) {
    return state.currentUser;
  }

  return state.utilityUser;
}

function makeUserId(state) {
  const isUtilityProfitApp = getIsUtilityProfitApp(state.analytics.screenId);
  const user = getUser(state);

  if (!isUtilityProfitApp) {
    return String(user.id || state.analytics.analyticsSessionId);
  }

  if (user.utilityUserId || user.id) {
    return `up-${user.utilityUserId || user.id}`;
  }

  return String(state.analytics.analyticsSessionId);
}

/**
 * Transforms a user data object to standard analytics traits.
 *
 * https://segment.com/docs/spec/identify/#traits
 *
 * @param  {Object} userData User data object to transform.
 * @return {Object} Standard identification traits.
 */
export function transformUserDataToSegmentIdentifyTraits(userData) {
  const [firstName, ...lastNameParts] = (userData.name || '').split(' ');

  return {
    avatar: userData.profileImage || undefined,
    email: userData.email || undefined,
    firstName: firstName || undefined,
    lastName: lastNameParts.join(' ') || undefined,
    name: userData.name || undefined,
    id: userData.id || undefined,
    phone: userData.phone || undefined,
    createdAt: userData.createdDate || undefined,
  };
}

export function transformUtilityUserToSegmentIdentifyTraits({ user, utilityActivation }) {
  const traits = {
    id: `up-${user.id}`, // This should be the same as the user ID in the identify call
    email: user.email,
    phone: user.phone,
    firstName: user.firstName,
    lastName: user.lastName,
    createdAt: user.createdAt,
  };

  if (utilityActivation) {
    traits.partnerSlug = utilityActivation.partner.slug;
    traits.activationShortId = utilityActivation.shortId;
    traits.leaseStartDate = utilityActivation.leaseStartDate;
    traits.addressStreetDisplay = utilityActivation.address?.streetDisplay;
    traits.addressDisplay = utilityActivation.address?.display;
    traits.addressStateCode = utilityActivation.address?.stateCode;
  }

  return traits;
}

/**
 * https://segment.com/docs/spec/common/
 *
 * @param  {Object} state
 * @return {Object} Common event parameters
 */
function makeCommonEventOptions(state, navigationContext) {
  const {
    analytics: { analyticsSessionId, utmValues, requestIp, location },
  } = state;

  const user = getUser(state);

  const eventOptions = {
    context: {
      app: {
        name: 'SunroomWeb',
        namespace: `com.${NODE_ENV === 'production' ? 'production' : 'development'}.sunroom`,
      },
      ip: requestIp,
      location: location,
      screen: {
        width: navigationContext.browserWidth,
        height: navigationContext.browserHeight,
      },
    },
  };

  if (user.roles.includes(USER_ROLE_ANONYMOUS)) {
    eventOptions.anonymousId = analyticsSessionId;
    eventOptions.integrations = {
      All: true,
      'Customer.io': false,
      'Customer.io (Actions)': false,
    };
  } else if (user.id) {
    eventOptions.userId = makeUserId(state);
  }

  if (utmValues && typeof utmValues === 'object') {
    eventOptions.context.campaign = {
      name: utmValues.campaign || undefined,
      source: utmValues.source || undefined,
      medium: utmValues.medium || undefined,
      content: utmValues.content || undefined,
      term: utmValues.term || undefined,
    };
  }

  return eventOptions;
}

/**
 * Extracts relevant contextual data from a Window object.
 *
 * @param  {Window} _window  The global "window" object.
 * @return {Object} Relevant contextual metadata.
 */
function extractNavigationContextFromWindow(_window) {
  return {
    documentTitle: lodashGet('document.title', _window),
    documentReferrer: lodashGet('document.referrer', _window),
    locationHref: lodashGet('location.href', _window),
    locationPathname: lodashGet('location.pathname', _window),
    locationHostname: lodashGet('location.hostname', _window),
    locationSearch: lodashGet('location.search', _window),
    locationPort: lodashGet('location.port', _window),
    locationProtocol: lodashGet('location.protocol', _window),
    locationHash: lodashGet('location.hash', _window),
    browserWidth:
      _window.innerWidth ||
      lodashGet('documentElement.clientWidth', _window.document) ||
      lodashGet('body.clientWidth', _window.document),
    browserHeight:
      _window.innerHeight ||
      lodashGet('documentElement.clientHeight', _window.document) ||
      lodashGet('body.clientHeight', _window.document),
    scrollVertical:
      (_window.pageYOffset || lodashGet('documentElement.scrollTop', _window.document)) -
      (lodashGet('documentElement.clientTop', _window.document) || 0),
  };
}

export default {
  /**
   * Configures the analytics service instance.
   *
   * @param  {Window}   args._window  Window object.
   * @param  {Object}   args.store    Baobab store.
   * @param  {Function} args.onTrack  Function to be run after tracking an event.
   * @return {undefined}
   */
  configure({ _window, store, onTrack }) {
    this.store = store;
    this._window = _window;
    this.onTrack = onTrack;

    const { NODE_ENV, DATADOG_APP_ID, DATADOG_CLIENT_TOKEN, COMMIT_HASH } = store.get(['env']);

    if (this.isAnalyticsEnabled()) {
      datadogRum.init({
        applicationId: DATADOG_APP_ID,
        clientToken: DATADOG_CLIENT_TOKEN,
        site: 'datadoghq.com',
        service: 'up-app',
        env: NODE_ENV,
        // Specify a version number to identify the deployed version of your application in Datadog
        version: COMMIT_HASH,
        sessionSampleRate: NODE_ENV === 'production' ? 100 : 20,
        sessionReplaySampleRate: NODE_ENV === 'production' ? 100 : 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'allow',
      });
    }
  },

  /**
   * Checks if analytics is enabled for this user session.
   *
   * @return {boolean} True if analytics is enabled for this user session.
   */
  isAnalyticsEnabled() {
    const state = this.store.get();
    const locationPathname = lodashGet('location.pathname', this._window);

    return (
      process.browser &&
      state.analytics.isEnabled &&
      !state.analytics.userAgent.isCrawler &&
      (locationPathname.includes('/utility-profit') || locationPathname.includes('/utility-setup'))
    );
  },

  /**
   * Identifies a user.
   */
  identify() {
    const analyticsEnabled = this.isAnalyticsEnabled();
    const state = this.store.get();
    const user = getUser(state);
    const userId = makeUserId(state);
    const isAnonymous = user.roles.includes(USER_ROLE_ANONYMOUS);
    const isUtilityProfitApp = getIsUtilityProfitApp(state.analytics.screenId);
    const navigationContext = extractNavigationContextFromWindow(this._window);
    let segmentTraits = isAnonymous ? null : transformUserDataToSegmentIdentifyTraits(user);

    if (isUtilityProfitApp) {
      segmentTraits = transformUtilityUserToSegmentIdentifyTraits({
        user,
        utilityActivation: getUtilityActivationForContext(state),
      });
    }

    const eventParams = makeCommonEventOptions(state, navigationContext);

    debug('Identifying', {
      user: user,
      isEnabled: analyticsEnabled,
      segmentTraits: segmentTraits,
      navigationContext: navigationContext,
      eventParams: eventParams,
    });

    if (analyticsEnabled) {
      datadogRum.setUser({
        id: userId,
        name: user.name || lodashCompact([user.firstName, user.lastName]).join(' ') || null,
        phone: user.phone,
        email: user.email,
        isPartnerUser: !!user.utilityPartnerId,
      });

      this.safeAnalyticsCall('identify', userId, segmentTraits, eventParams);
    }
  },

  /**
   * Creates a user alias. This should be called after a user signup to attribute his old events to his new ID.
   *
   * https://segment.com/docs/spec/alias/
   *
   * @param {string|number} userId     The new user ID.
   * @param {string|number} userPrevId The previous user ID.
   */
  alias(userId, userPrevId) {
    const analyticsEnabled = this.isAnalyticsEnabled();

    debug('Aliasing', { userId, userPrevId, analyticsEnabled });

    if (analyticsEnabled) {
      this.safeAnalyticsCall('alias', String(userId), userPrevId);
    }
  },

  /**
   * Clears the analytics user state.
   */
  logout() {
    const analyticsEnabled = this.isAnalyticsEnabled();
    debug('Logging out', { analyticsEnabled });

    // Reset Datadog RUM user information

    if (analyticsEnabled) {
      datadogRum.resetUser();

      // NOTE(@d-oliveros): window.analytics.reset() is the "new" way of resetting cookies.
      const analyticsResetWasCalled = this.safeAnalyticsCall('reset');

      // NOTE(@d-oliveros): window.analytics.user.logout() is the "old" way of resetting cookies. Not sure if needed anymore.
      if (!analyticsResetWasCalled && lodashGet('analytics.user.logout', this._window)) {
        this._window.analytics.user.logout();
      }
      if (lodashGet('mixpanel.reset', this._window)) {
        this._window.mixpanel.reset();
      }
    }
  },

  /**
   * Tracks an event.
   *
   * @param {string} eventType                    The event type.
   * @param {Object} originalEventProps           Extra properties to be attached to this event.
   * @param {Object} originalEventTrackingOptions Extra segment options to enable/disable only certain integrations, etc.
   */
  track(eventType, originalEventProps = {}, originalEventTrackingOptions = {}) {
    const analyticsEnabled = this.isAnalyticsEnabled();
    const state = this.store.get();
    const navigationContext = extractNavigationContextFromWindow(this._window);

    const applicationContext = makeApplicationContext({
      state: state,
      navigationContext: navigationContext,
    });

    const eventPayload = { ...applicationContext, ...originalEventProps };

    const commonEventOptions = makeCommonEventOptions(state, navigationContext);
    const eventOptions = { ...commonEventOptions, ...originalEventTrackingOptions };

    const event = {
      type: eventType,
      payload: eventPayload,
      options: eventOptions,
    };

    debug(`Tracking "${event.type}"`, { event, analyticsEnabled });

    // Log specific high-value events for monitoring
    if (
      [
        'LANDING_PAGE_VIEW',
        'UTILITY_SETUP_INTERNET_PLAN_SELECTED',
        'UTILITY_SETUP_PROVIDER_PLAN_CONFIRMATION_LANDED',
        'UTILITY_SETUP_PROVIDER_PLAN_CONFIRM_ORDER_CLICKED',
      ].includes(event.type)
    ) {
      log.info(`Triggered event ${event.type} for activation ${eventPayload?.utilityActivationId}`);
    }

    if (analyticsEnabled) {
      // Track the event through Segment
      this.safeAnalyticsCall('track', event.type, event.payload, event.options);

      this._eventLog = this._eventLog || [];
      this._eventLog.push({
        timestamp: Date.now(),
        event: event,
      });

      // Run any custom tracking handler
      if (typeof this.onTrack === 'function') {
        this.onTrack(event);
      }
    }
  },

  /**
   * Tracks a page view.
   */
  pageview() {
    const analyticsEnabled = this.isAnalyticsEnabled();
    const state = this.store.get();
    const navigationContext = extractNavigationContextFromWindow(this._window);

    if (analyticsEnabled) {
      const applicationContext = makeApplicationContext({
        state: state,
        navigationContext: extractNavigationContextFromWindow(this._window),
      });
      const eventOptions = makeCommonEventOptions(state, navigationContext);

      this.safeAnalyticsCall(
        'page',
        state.analytics.screenId,
        navigationContext.documentTitle,
        applicationContext,
        eventOptions,
      );
    }
  },

  /**
   * Makes a safe "window.analytics" call.
   *
   * @param  {string}  methodName  Method in Segment's analytics library to call
   * @param  {Array}   args        Destructured array with remaining function arguments
   * @return {boolean}             True if the method was called
   */
  safeAnalyticsCall(methodName, ...args) {
    assert(methodName, 'methodName is required.');

    if (typeof lodashGet(`analytics.${methodName}`, this._window) !== 'function') {
      return false;
    }

    try {
      this._window.analytics[methodName](...args);
    } catch (error) {
      log.error(error);
    }

    return true;
  },
};
